import { useMemo } from "react";
import { Card, Col, Row, Table, Form, Button, Spinner } from "react-bootstrap";
import { BiCart, BiWallet, BiSupport, BiUser, BiTrendingUp, BiRefresh } from "react-icons/bi";
import { formatNumber, formatRawNumber } from "../../Utilities/numbers";
import EChartsDoughnut from "../Charts/EChartsDoughnut";
import EChartsHorizontalBar from "../Charts/EChartsHorizontalBar";
import EChartsDualAxisLine from "../Charts/EChartsDualAxisLine";
import EChartsPaymentMethodsLine from "../Charts/EChartsPaymentMethodsLine";
import EChartsOrdersByHour from "../Charts/EChartsOrdersByHour";
import EChartsStatusCombo from "../Charts/EChartsStatusCombo";
import EChartsHeatmap from "../Charts/EChartsHeatmap";
import EChartsTreemap from "../Charts/EChartsTreemap";
import EChartsGauge from "../Charts/EChartsGauge";
import StatCard from "../DashboardCards/StatCard/StatCard";
import ReactSelect from "../ReactSelect";
import { getDropdownShops } from "../../api/getapi";
import { ECHART_COLORS } from "../../Utilities/echartsTheme";
import moment from "moment";

const timePeriodOptions = [
  { value: "all-time", label: "All Time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this-week", label: "This Week" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "last-year", label: "Last Year" },
  { value: "custom", label: "Custom Range" }
];

const orderRangeOptions = [
  { value: "last-7-days", label: "Last 7 Days" },
  { value: "last-30-days", label: "Last 30 Days" },
  { value: "last-90-days", label: "Last 90 Days" },
  { value: "this-month", label: "This Month" },
  { value: "this-year", label: "This Year" },
  { value: "custom", label: "Custom" }
];

const orderStatusOptions = [
  "All",
  "Pending",
  "In Progress",
  "Completed",
  "Cancelled",
  "Partially Fulfilled",
  "Returned",
  "Confirmed",
  "Delivered",
  "Picked",
];

const OrderAnalyticsSection = ({
  marketplaceOrderInsights,
  revenueInsights,
  productInsights,
  categoryInsights,
  supportOrderInsights,
  shopFulfillmentInsights,
  ordersPerDay,
  ordersByPaymentMethod,
  ordersByHour,
  timePeriod,
  onTimePeriodChange,
  customDateFrom,
  onCustomDateFromChange,
  customDateTo,
  onCustomDateToChange,
  shop,
  onShopChange,
  status,
  onStatusChange,
  ordersRange,
  onOrdersRangeChange,
  ordersDateFrom,
  onOrdersDateFromChange,
  ordersDateTo,
  onOrdersDateToChange,
  onRefresh,
  loading = false
}) => {
  const spinKeyframes = `@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } } .spin { animation: spin 0.8s linear infinite; display: inline-block; }`;
  const general = marketplaceOrderInsights?.general || {};
  const orderStatus = general.orderStatusBreakdown || {};
  const sourceStatus = general.sourceStatusBreakdown || {};

  const avgOrderValuePerDay = useMemo(() => {
    const activeDays = Number(general.active_days) || 0;
    if (activeDays === 0) return 0;
    return (Number(general.total_grand_total) || 0) / activeDays;
  }, [general.active_days, general.total_grand_total]);

  const avgOrdersPerDay = useMemo(() => {
    const activeDays = Number(general.active_days) || 0;
    if (activeDays === 0) return 0;
    return (Number(general.total_orders) || 0) / activeDays;
  }, [general.active_days, general.total_orders]);

  const conversionRate = useMemo(() => {
    const total = Number(general.total_orders) || 0;
    const delivered = Number(general.delivered_orders) || 0;
    if (total === 0) return 0;
    return (delivered / total) * 100;
  }, [general.total_orders, general.delivered_orders]);

  const avgDeliveredPerDay = useMemo(() => {
    if (!ordersPerDay || ordersPerDay.length === 0) return 0;
    const total = ordersPerDay.reduce((sum, d) => sum + (Number(d.delivered_count) || 0), 0);
    return total / ordersPerDay.length;
  }, [ordersPerDay]);

  const perDayAvgOrderValue = useMemo(() => {
    if (!ordersPerDay || ordersPerDay.length === 0) return 0;
    const totalRevenue = ordersPerDay.reduce((sum, d) => sum + (Number(d.revenue) || 0), 0);
    return totalRevenue / ordersPerDay.length;
  }, [ordersPerDay]);

  const topProducts = useMemo(
    () =>
      productInsights.slice(0, 10).map((p) => ({
        label: p.product_title,
        value: p.total_quantity,
        revenue: p.total_revenue,
        ...p
      })),
    [productInsights]
  );

  const revenueByShop = useMemo(
    () =>
      (revenueInsights?.perShop || [])
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10)
        .map((s) => ({
          label: s.shop_name,
          value: s.revenue,
          ...s
        })),
    [revenueInsights]
  );

  const categoryOverall = useMemo(
    () => (categoryInsights?.overall || []).map((c) => ({
      label: c.category,
      value: Number(c.total_value),
      quantity: Number(c.total_quantity),
      orders: c.order_count,
      products: c.unique_products,
      ...c
    })),
    [categoryInsights]
  );

  const categoryByStatus = useMemo(() => {
    const rows = categoryInsights?.byStatus || [];
    const categories = [...new Set(rows.map((r) => r.category))];
    const statuses = [...new Set(rows.map((r) => r.status))];
    return {
      categories,
      statuses,
      matrix: rows
    };
  }, [categoryInsights]);

  const showCustomDates = ordersRange === "custom";

  const loadShops = async (input) => {
    try {
      const res = await getDropdownShops(input);
      const shops = (res.data || []).map((cat) => ({
        value: cat,
        label: `${cat.id} - ${cat.shop_name}`,
        name: `${cat.shop_name} [${cat.id}]`
      }));
      shops.unshift({ value: null, label: "All Shops", name: "All Shops" });
      return shops;
    } catch {
      return [{ value: null, label: "All Shops", name: "All Shops" }];
    }
  };

  const handleShopChange = (selected) => {
    onShopChange(selected || null);
  };

  const handleRangeChange = (e) => {
    onOrdersRangeChange(e.target.value);
  };

  const maxFromDate = useMemo(() => {
    return ordersDateTo ? moment(ordersDateTo).format("YYYY-MM-DD") : undefined;
  }, [ordersDateTo]);

  const maxToDate = moment().format("YYYY-MM-DD");

  return (
    <>
      <style>{spinKeyframes}</style>
      <Row className="py-2 align-items-center">
        <Col md="6">
          <h5 className="fw-bold">Marketplace Order Analytics</h5>
          <p className="text-muted mb-0">Order insights, revenue trends, and product performance.</p>
        </Col>
        <Col md="6" className="text-md-end mt-3 mt-md-0">
          <Button
            variant="light"
            size="sm"
            className="me-2 border"
            onClick={onRefresh}
            disabled={loading}
          >
            <BiRefresh size={20} className={loading ? "spin" : ""} />
          </Button>
          <Form.Select
            size="sm"
            value={timePeriod}
            onChange={(e) => onTimePeriodChange(e.target.value)}
            style={{ width: 150, display: "inline-block" }}
          >
            {timePeriodOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Form.Select>
          {timePeriod === "custom" && (
            <div className="d-flex align-items-end justify-content-end gap-1 ms-2 mt-2">
              <Form.Control
                type="date"
                size="sm"
                value={customDateFrom || ""}
                onChange={(e) => onCustomDateFromChange(e.target.value)}
                max={customDateTo || moment().format("YYYY-MM-DD")}
                style={{ width: 150 }}
              />
              <span className="text-muted">–</span>
              <Form.Control
                type="date"
                size="sm"
                value={customDateTo || ""}
                onChange={(e) => onCustomDateToChange(e.target.value)}
                min={customDateFrom}
                max={moment().format("YYYY-MM-DD")}
                style={{ width: 150 }}
              />
            </div>
          )}
        </Col>
      </Row>

      <Card className="p-3 mb-4 border-0 shadow-sm">
        <div className="d-flex flex-wrap gap-2 align-items-center">
          <div style={{ width: 220 }}>
            <ReactSelect
              placeholder="Select shop"
              value={shop}
              searchable={true}
              options={loadShops}
              handleChange={handleShopChange}
              onInputChange={(newInput) => newInput}
            />
          </div>
          <Form.Select
            size="sm"
            value={status || "All"}
            onChange={(e) => onStatusChange(e.target.value === "All" ? "" : e.target.value)}
            style={{ width: 160 }}
          >
            {orderStatusOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </Form.Select>
          <div className="d-flex align-items-center gap-1 ms-auto">
            <small className="text-muted text-nowrap fw-medium">Per-Day Chart:</small>
            <Form.Select
              size="sm"
              value={ordersRange}
              onChange={handleRangeChange}
              style={{ width: 150 }}
            >
              {orderRangeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Form.Select>
          </div>
          {showCustomDates && (
            <>
              <Form.Control
                type="date"
                size="sm"
                value={ordersDateFrom || ""}
                onChange={(e) => onOrdersDateFromChange(e.target.value)}
                max={maxFromDate}
                style={{ width: 150 }}
              />
              <Form.Control
                type="date"
                size="sm"
                value={ordersDateTo || ""}
                onChange={(e) => onOrdersDateToChange(e.target.value)}
                max={maxToDate}
                min={ordersDateFrom}
                style={{ width: 150 }}
              />
            </>
          )}
        </div>
      </Card>

      {loading && (
        <div className="text-center mt-4 mb-4">
          <Spinner animation="border" />
        </div>
      )}

      {!loading && (
        <>
      <Row className="g-2 mt-1 mb-2">
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Total Orders"
            count={formatRawNumber(general.total_orders)}
            icon={<BiCart />}
            color={ECHART_COLORS.primary}
          />
        </Col>
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Delivered Orders"
            count={formatRawNumber(general.delivered_orders)}
            icon={<BiTrendingUp />}
            color={ECHART_COLORS.success}
          />
        </Col>
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Conversion Rate"
            count={`${formatNumber(conversionRate)}%`}
            icon={<BiTrendingUp />}
            color={ECHART_COLORS.emerald}
          />
        </Col>
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Orders Value"
            count={`Rs. ${formatNumber(revenueInsights?.general?.total_revenue)}`}
            icon={<BiWallet />}
            color={ECHART_COLORS.accent}
          />
        </Col>
      </Row>
      <Row className="g-2 mb-2">
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Avg Orders/Day"
            count={formatNumber(avgOrdersPerDay)}
            icon={<BiTrendingUp />}
            color={ECHART_COLORS.teal}
          />
        </Col>
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Avg Order Value/Day"
            count={`Rs. ${formatNumber(avgOrderValuePerDay)}`}
            icon={<BiTrendingUp />}
            color={ECHART_COLORS.primaryLight}
          />
        </Col>
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Support Orders"
            count={formatNumber(supportOrderInsights?.general?.support_orders)}
            icon={<BiSupport />}
            color={ECHART_COLORS.purple}
          />
        </Col>
        <Col md="6" lg="3" sm="12" xl="3">
          <StatCard
            title="Customer Orders"
            count={formatNumber(supportOrderInsights?.general?.customer_orders)}
            icon={<BiUser />}
            color={ECHART_COLORS.success}
          />
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="12" sm="12" xl="12">
          <Card className="border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Orders by Status (Count & Value)</h6>
            <div style={{ height: 340 }}>
              <EChartsStatusCombo data={revenueInsights?.general?.byStatus || []} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="5" sm="12" xl="5">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Category Distribution by Orders</h6>
            <div style={{ height: 340 }}>
              <EChartsTreemap data={categoryOverall} valueKey="orders" labelKey="label" />
            </div>
          </Card>
        </Col>
        <Col md="12" lg="7" sm="12" xl="7">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Orders by Category (Count & Value)</h6>
            <div style={{ height: 340 }}>
              <EChartsStatusCombo
                data={categoryInsights?.overall || []}
                labelKey="category"
                countKey="order_count"
                valueKey="total_value"
                countLabel="Orders"
                valueLabel="Value"
                valuePrefix="Rs "
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="12" sm="12" xl="12">
          <Card className="border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Category × Status Heatmap</h6>
            <div style={{ height: 380 }}>
              <EChartsHeatmap
                categories={categoryByStatus.categories}
                statuses={categoryByStatus.statuses}
                matrix={categoryByStatus.matrix}
                valueKey="total_value"
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="12" sm="12" xl="12">
          <Card className="border-0 shadow-sm p-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="fw-bold mb-0">Orders & Value Per Day</h6>
              <div className="text-muted" style={{ fontSize: 12 }}>
                <span className="me-3">Avg Delivered/Day: <strong>{formatNumber(avgDeliveredPerDay)}</strong></span>
                <span>Avg Value/Day: <strong>Rs {formatNumber(perDayAvgOrderValue)}</strong></span>
              </div>
            </div>
            <div style={{ height: 420 }}>
              <EChartsDualAxisLine
                data={ordersPerDay}
                dateFrom={ordersDateFrom}
                dateTo={ordersDateTo}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="12" sm="12" xl="12">
          <Card className="border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Orders by Payment Method (Over Time)</h6>
            <div style={{ height: 420 }}>
              <EChartsPaymentMethodsLine data={ordersByPaymentMethod} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="12" sm="12" xl="12">
          <Card className="border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Orders by Hour of Day</h6>
            <div style={{ height: 380 }}>
              <EChartsOrdersByHour data={ordersByHour} />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="6" lg="4" sm="12" xl="4">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Order Status Distribution</h6>
            <div style={{ height: 280 }}>
              <EChartsDoughnut data={orderStatus} />
            </div>
          </Card>
        </Col>
        <Col md="6" lg="4" sm="12" xl="4">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Order Source Status</h6>
            <div style={{ height: 280 }}>
              <EChartsDoughnut data={sourceStatus} />
            </div>
          </Card>
        </Col>
        <Col md="12" lg="4" sm="12" xl="4">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Support vs Customer Orders</h6>
            <div style={{ height: 280 }}>
              <EChartsDoughnut
                data={{
                  Support: supportOrderInsights?.general?.support_orders || 0,
                  Customer: supportOrderInsights?.general?.customer_orders || 0
                }}
                colors={[ECHART_COLORS.purple, ECHART_COLORS.success]}
              />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="6" sm="12" xl="6">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Top 10 Products by Quantity</h6>
            <div style={{ height: 340 }}>
              <EChartsHorizontalBar data={topProducts} label="Quantity" multiColor />
            </div>
          </Card>
        </Col>
        <Col md="12" lg="6" sm="12" xl="6">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Top 10 Shops by Revenue</h6>
            <div style={{ height: 340 }}>
              <EChartsHorizontalBar data={revenueByShop} label="Revenue" multiColor />
            </div>
          </Card>
        </Col>
      </Row>

      <Row className="g-2 mb-2">
        <Col md="12" lg="6" sm="12" xl="6">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Top Selling Products</h6>
            <div className="table-responsive">
              <Table hover size="sm" className="align-middle">
                <thead className="bg-light">
                  <tr>
                    <th className="text-center" style={{ width: 40 }}>#</th>
                    <th>Product</th>
                    <th>Shop</th>
                    <th className="text-end">Qty</th>
                    <th className="text-end">Revenue</th>
                  </tr>
                </thead>
                <tbody>
                  {productInsights.slice(0, 10).map((product, index) => (
                    <tr key={index}>
                      <td className="text-center text-muted">{index + 1}</td>
                      <td>
                        <div className="fw-medium">{product.product_title}</div>
                        <small className="text-muted">{product.category}</small>
                      </td>
                      <td>{product.shop_name}</td>
                      <td className="text-end">{formatNumber(product.total_quantity)}</td>
                      <td className="text-end">{formatNumber(product.total_revenue)}</td>
                    </tr>
                  ))}
                  {productInsights.length === 0 && (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No product data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card>
        </Col>
        <Col md="12" lg="6" sm="12" xl="6">
          <Card className="h-100 border-0 shadow-sm p-3">
            <h6 className="fw-bold mb-3">Shop Fulfillment</h6>
            {shopFulfillmentInsights.length > 0 ? (
              <Row className="g-2">
                {shopFulfillmentInsights.slice(0, 12).map((shop, idx) => {
                  const rate = shop.attempted ? Math.round((shop.fulfilled / shop.attempted) * 100) : 0;
                  return (
                    <Col key={idx} xs="6" md="4" className="mb-2">
                      <div className="text-center">
                        <div style={{ height: 100 }}>
                          <EChartsGauge value={rate} title={shop.shop_name} />
                        </div>
                        <div className="fw-medium text-truncate" style={{ fontSize: 11 }} title={shop.shop_name}>
                          {shop.shop_name}
                        </div>
                        <small className="text-muted" style={{ fontSize: 10 }}>
                          {formatNumber(shop.fulfilled)}/{formatNumber(shop.attempted)}
                        </small>
                      </div>
                    </Col>
                  );
                })}
              </Row>
            ) : (
              <div className="d-flex align-items-center justify-content-center h-100 text-muted py-3">
                No fulfillment data available
              </div>
            )}
          </Card>
        </Col>
      </Row>
        </>
      )}
    </>
  );
};

export default OrderAnalyticsSection;
